import { useState, useEffect, useRef } from 'react';
import { API_URL, SITE_URL } from '../config';
import colors from '../theme/colors';

/**
 * Compact horizontal HikeCard designed for iframe embedding.
 * Adapts to container size - works in any iframe dimensions.
 */
const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export default function EmbedHikeCard({ hikeId, theme = 'light' }) {
  const [hike, setHike] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);

  // Detect container size and adapt layout
  useEffect(() => {
    const checkSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setIsCompact(width < 400);
      }
    };

    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  useEffect(() => {
    const fetchHike = async () => {
      try {
        const response = await fetch(`${API_URL}/hikes/embed/${hikeId}`);
        if (!response.ok) {
          throw new Error('Escursione non trovata');
        }
        const data = await response.json();
        setHike(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (hikeId) {
      fetchHike();
    }
  }, [hikeId]);

  const getTagColor = (tag) => {
    const tagColors = colors.tags[tag] || colors.tags.default;
    return `${tagColors.bg} ${tagColors.text}`;
  };

  const formatDate = () => {
    if (!hike) return '';
    const startDate = capitalizeFirst(new Date(hike.data_inizio).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).replace(', ', ' '));

    if (hike.data_fine) {
      const endDate = capitalizeFirst(new Date(hike.data_fine).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).replace(', ', ' '));
      return `${startDate} - ${endDate}`;
    }
    return startDate;
  };

  const handleClick = () => {
    const baseUrl = SITE_URL || window.location.origin.replace(/\/embed.*/, '');
    // If scheda is hidden, redirect to home page instead of hike detail
    const url = hike?.is_scheda_hidden ? baseUrl : `${baseUrl}/hike/${hikeId}`;
    window.open(url, '_blank');
  };

  const isDark = theme === 'dark';
  const bgClass = isDark ? 'bg-gray-800' : 'bg-white';
  const textClass = isDark ? 'text-gray-100' : 'text-gray-800';
  const textMutedClass = isDark ? 'text-gray-400' : 'text-gray-500';
  const borderClass = isDark ? 'border-gray-700' : 'border-gray-200';

  if (loading) {
    return (
      <div className={`${bgClass} rounded-lg shadow-sm p-4 flex items-center justify-center h-24 border ${borderClass}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${bgClass} rounded-lg shadow-sm p-4 flex items-center justify-center h-24 border ${borderClass}`}>
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  if (!hike) {
    return null;
  }

  const isGiovani = hike.categories && hike.categories.some(c => c.toLowerCase() === 'giovani');

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      className={`${bgClass} rounded-lg shadow-sm hover:shadow-md overflow-hidden transition cursor-pointer border ${borderClass} flex w-full h-full`}
    >
      {/* Image - hidden on compact */}
      {!isCompact && (
        <div className="shrink w-32 h-full">
          <img
            src={hike.photos && hike.photos.length > 0
              ? (hike.photos[0].thumbnail_url || hike.photos[0].media_url)
              : hike.url_foto
            }
            alt={hike.titolo}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* Content - grows to fill space, never shrinks */}
      <div className="flex-1 min-w-0 h-full flex flex-col justify-center items-center gap-1 text-center px-2 sm:px-3">
        {/* Top row: Date (prominent) and badges */}
        <div className="flex items-center justify-center gap-2">
          <span className="text-sm sm:text-base font-bold text-blue-600 whitespace-nowrap">
            {formatDate()}
          </span>
          {/* Status badges - hide on very small */}
          <div className="flex gap-1 flex-shrink-0">
            {hike.is_cancelled && (
              <span className="px-1 sm:px-1.5 py-0.5 rounded text-xs font-bold bg-red-600 text-white">
                {isCompact ? '!' : 'Annullata'}
              </span>
            )}
            {hike.tags && hike.tags.slice(0, 2).map((tag, index) => (
              <span
                key={index}
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${getTagColor(tag)}`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Middle row: Title */}
        <h3 className={`text-xs sm:text-sm font-semibold ${textClass} truncate max-w-full`}>
          {hike.titolo}
        </h3>

        {/* Organizers */}
        {hike.organizers && hike.organizers.length > 0 && (
          <p className={`text-xs ${textMutedClass} truncate max-w-full`}>
            {hike.organizers.map(org => org.display_name).join(', ')}
          </p>
        )}

        {/* Bottom row: Location and metadata */}
        <div className="flex items-center justify-center gap-3 text-xs">
          {hike.luogo && (
            <span className={`${textMutedClass} truncate`}>
              {hike.luogo}
            </span>
          )}
          {hike.metadata?.distanza && (
            <span className={textMutedClass}>
              {hike.metadata.distanza}
            </span>
          )}
          {hike.metadata?.dislivello && (
            <span className={textMutedClass}>
              {hike.metadata.dislivello}
            </span>
          )}
        </div>
      </div>

      {/* Right side - arrow always visible; giovani icon hidden in compact mode */}
      <div className={`shrink flex items-center justify-end gap-1 sm:gap-2 px-2 sm:px-3 h-full ${textMutedClass}`} style={{ minWidth: '32px' }}>
        {isGiovani && !isCompact && (
          <div className="flex flex-col items-center min-w-0">
            <img
              src="/logo_giovani.jpg"
              alt="CAI Giovani"
              className="h-12 sm:h-16 rounded-sm object-contain"
            />
            <span className={`text-xs font-medium ${textClass} mt-0.5 truncate`}>Gruppo Giovani</span>
          </div>
        )}
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}
